import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Lock, Zap, Loader2, Check } from "lucide-react";

const PRO_PERKS = [
  "AI Trade Advisor grounded in live values",
  "Multi-league portfolio & exposure",
  "DME League Intelligence (VAR, desperation, pick-sim)",
  "Trade Finder, Heat Map & War Room",
];

export const ProGate = ({ children, feature = "This tool" }) => {
  const { access, loading } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);

  if (loading) {
    return (
      <div className="flex justify-center py-24" data-testid="pro-gate-loading">
        <Loader2 className="w-6 h-6 spin text-[var(--accent)]" />
      </div>
    );
  }
  if (access?.has_access) return children;

  const startTrial = async () => {
    setBusy(true);
    try {
      const r = await api.post("/billing/checkout", { origin_url: window.location.origin, with_trial: true });
      window.location.href = r.data.url;
    } catch {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-[60vh] fade-up" data-testid="pro-gate">
      {/* Blurred decorative backdrop hinting at hidden content */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none select-none" aria-hidden="true">
        <div className="blur-md opacity-40 space-y-4 p-2">
          <div className="grid grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={`s-${i}`} className="card-bordered p-5">
                <div className="h-2 w-16 bg-[var(--border)] rounded mb-3" />
                <div className="h-7 w-24 bg-[var(--accent)]/30 rounded" />
              </div>
            ))}
          </div>
          {[...Array(5)].map((_, i) => (
            <div key={`r-${i}`} className="card-bordered p-4 flex items-center justify-between">
              <div className="h-3 w-40 bg-[var(--border)] rounded" />
              <div className="h-3 w-12 bg-[var(--accent)]/30 rounded" />
            </div>
          ))}
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[var(--bg)]/70 to-[var(--bg)]" />
      </div>

      {/* Upgrade card */}
      <div className="relative z-10 flex items-center justify-center pt-16 px-4">
        <div className="card-bordered neon-border p-8 md:p-10 max-w-md w-full text-center" data-testid="pro-gate-card">
          <div className="w-12 h-12 rounded-full bg-[var(--accent)]/15 border border-[var(--accent)]/40 flex items-center justify-center mx-auto mb-5">
            <Lock className="w-5 h-5 text-[var(--accent)]" />
          </div>
          <div className="text-[10px] tracking-[0.25em] text-[var(--accent)] font-mono mb-2">PRO FEATURE</div>
          <h2 className="font-display text-2xl font-bold mb-2">{feature} is part of Dynasty Metrics Pro</h2>
          <p className="text-sm text-[var(--text-2)] mb-6">
            Your free plan includes the Trade Calculator, Power Rankings, player values & market trends.
            Unlock the full unfair edge with Pro.
          </p>
          <ul className="text-left space-y-2 mb-7">
            {PRO_PERKS.map((p) => (
              <li key={p} className="flex items-start gap-2 text-sm">
                <Check className="w-4 h-4 text-[var(--accent)] mt-0.5 shrink-0" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
          <button onClick={startTrial} disabled={busy} className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="pro-gate-trial-btn">
            {busy ? <Loader2 className="w-4 h-4 spin" /> : <Zap className="w-4 h-4" />}
            Start 7-day free trial
          </button>
          <div className="text-[11px] text-[var(--text-3)] mt-2">Card required · $9.99/mo after trial · cancel anytime</div>
          <button onClick={() => nav("/pricing")} className="text-xs text-[var(--accent)] hover:underline mt-4" data-testid="pro-gate-plans-btn">
            View plans &amp; redeem a beta code
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProGate;
